import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useIntegrationsHome } from '../hooks';
import { DEFAULT_INTEGRATIONS_TAB } from '../constants';

const IntegrationsContext = createContext(null);

/**
 * Single source of shared Integrations-home state.
 *
 * ALL keys are declared here from the start (even when empty/no-op) so the home
 * child stories (CT-4157 Hero, CT-4158 Tab Strip, CT-4159 Band, and the
 * CT-4160/CT-4162 Already Integrated tab that reads `searchQuery`) fill in their
 * own block without fighting over this file. Each grouping is annotated with the
 * story that owns it.
 */
export const IntegrationsProvider = ({ children }) => {
  const {
    stats,
    statsLoading,
    statsError,
    marketplaceStats,
    marketplaceStatsLoading,
    attentionItems,
    attentionLoading,
    attentionError,
  } = useIntegrationsHome();

  // search  → CT-4157 (Hero writes here; the active tab reads it to filter)
  const [searchQuery, setSearchQuery] = useState('');
  const clearSearch = useCallback(() => setSearchQuery(''), []);

  // tabs    → CT-4158
  const [activeTab, setActiveTab] = useState(DEFAULT_INTEGRATIONS_TAB);

  // Request Integration modal → CT-4175/4176/4177
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [requestPrefill, setRequestPrefill] = useState(null);
  const openRequestIntegration = useCallback((payload) => {
    // Only keep a real prefill object (e.g. `{ prefillName }`). Entry points like
    // the Hero anchor / Tab Strip pass this straight to `onClick`, so guard against
    // storing the click event as prefill (truthy but with no `prefillName`).
    const prefill =
      payload && typeof payload === 'object' && 'prefillName' in payload ? payload : null;
    setRequestPrefill(prefill);
    setIsRequestOpen(true);
  }, []);
  const closeRequestIntegration = useCallback(() => {
    setIsRequestOpen(false);
    setRequestPrefill(null);
  }, []);
  const openBuildYourOwn = useCallback(() => {
    // TODO(CT-3894): open the Build Your Own chooser. Deferred in V1 (Coming soon).
  }, []);

  const value = useMemo(
    () => ({
      // stats + search → CT-4157
      stats,
      statsLoading,
      statsError,
      searchQuery,
      setSearchQuery,
      clearSearch,
      // tabs + actions → CT-4158
      activeTab,
      setActiveTab,
      alreadyIntegratedCount: stats.active_count ?? 0,
      marketplaceCount: marketplaceStats.catalog_count ?? 0,
      marketplaceStats,
      marketplaceStatsLoading,
      openRequestIntegration,
      closeRequestIntegration,
      isRequestOpen,
      requestPrefill,
      openBuildYourOwn,
      // band → CT-4159
      attentionItems,
      attentionLoading,
      attentionError,
    }),
    [
      stats,
      statsLoading,
      statsError,
      searchQuery,
      clearSearch,
      activeTab,
      marketplaceStats,
      marketplaceStatsLoading,
      openRequestIntegration,
      closeRequestIntegration,
      isRequestOpen,
      requestPrefill,
      openBuildYourOwn,
      attentionItems,
      attentionLoading,
      attentionError,
    ],
  );

  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
};

export const useIntegrations = () => {
  const ctx = useContext(IntegrationsContext);
  if (!ctx) throw new Error('useIntegrations must be used inside IntegrationsProvider');
  return ctx;
};
